"use client";

import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Calendar,
} from "lucide-react";
import Image from "next/image";

const REFRESH_INTERVAL = 30000;

interface NhlTim {
  id: number;
  nazov: string;
  skratka: string;
  logo_url: string;
  farba_pozadia: string;
}

interface NhlZapasData {
  id: number;
  espn_game_id: string;
  datum_zaciatku: string;
  stav_typ: string;
  stav_nazov: string;
  perioda: number;
  cas_zapasu: string | null;
  domaci_skore: number;
  hostiaci_skore: number;
  short_name: string;
  full_name: string;
  venue_nazov: string | null;
  domaci_tim: NhlTim | null;
  hostiaci_tim: NhlTim | null;
}

interface ApiResponse {
  zapasy: NhlZapasData[];
  datum: string;
  kalendar?: string[];
  source: string;
  cached: boolean;
  pocet: number;
  message?: string;
}

function getStatusText(
  stav: string,
  perioda: number,
  cas: string | null,
): string {
  if (stav === "STATUS_SCHEDULED") return "Naplánované";
  if (stav === "STATUS_IN_PROGRESS") return `${perioda}.T ${cas || ""}`;
  if (stav === "STATUS_FINAL") return "Koniec";
  if (stav === "STATUS_HALFTIME") return "Prestávka";
  return stav.replace("STATUS_", "");
}

function getStatusColor(stav: string): string {
  if (stav === "STATUS_IN_PROGRESS") return "bg-red-500";
  if (stav === "STATUS_FINAL") return "bg-muted-foreground";
  return "bg-blue-500";
}

function ZapasKarta({
  zapas,
  onClick,
}: {
  zapas: NhlZapasData;
  onClick: () => void;
}) {
  const jeZive = zapas.stav_typ === "STATUS_IN_PROGRESS";
  const jeUkonceny = zapas.stav_typ === "STATUS_FINAL";

  return (
    <Card
      className={`border-border bg-transparent hover:bg-muted/5 cursor-pointer transition-all ${
        jeZive ? "border-red-500/50 shadow-red-500/10 shadow-lg" : ""
      }`}
      onClick={onClick}
    >
      <CardContent className="p-6">
        {/* Status */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${getStatusColor(zapas.stav_typ)}`}
            />
            <span className="text-xs text-muted-foreground">
              {getStatusText(zapas.stav_typ, zapas.perioda, zapas.cas_zapasu)}
            </span>
          </div>
          {jeZive && (
            <Badge variant="destructive" className="text-xs px-2 py-0">
              LIVE
            </Badge>
          )}
        </div>

        {/* Tímy a skóre */}
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          {/* Domáci */}
          <div className="flex-1 flex flex-col items-center gap-2 min-w-0">
            {zapas.domaci_tim?.logo_url && (
              <Image
                src={zapas.domaci_tim.logo_url}
                alt={zapas.domaci_tim.skratka}
                width={40}
                height={40}
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
              />
            )}
            <div className="text-center w-full px-1">
              <p className="font-semibold text-xs sm:text-sm leading-tight wrap-break-word hyphens-auto">
                {zapas.domaci_tim?.nazov || zapas.domaci_tim?.skratka || "TBD"}
              </p>
            </div>
          </div>

          {/* Skóre */}
          <div className="flex items-center gap-2 sm:gap-3 px-1 sm:px-3 shrink-0">
            <span
              className={`text-xl sm:text-2xl lg:text-3xl font-bold ${jeUkonceny && zapas.domaci_skore > zapas.hostiaci_skore ? "text-primary" : ""}`}
            >
              {zapas.domaci_skore}
            </span>
            <span className="text-muted-foreground text-base sm:text-lg lg:text-xl">
              :
            </span>
            <span
              className={`text-xl sm:text-2xl lg:text-3xl font-bold ${jeUkonceny && zapas.hostiaci_skore > zapas.domaci_skore ? "text-primary" : ""}`}
            >
              {zapas.hostiaci_skore}
            </span>
          </div>

          {/* Hostia */}
          <div className="flex-1 flex flex-col items-center gap-2 min-w-0">
            {zapas.hostiaci_tim?.logo_url && (
              <Image
                src={zapas.hostiaci_tim.logo_url}
                alt={zapas.hostiaci_tim.skratka}
                width={40}
                height={40}
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
              />
            )}
            <div className="text-center w-full px-1">
              <p className="font-semibold text-xs sm:text-sm leading-tight wrap-break-word hyphens-auto">
                {zapas.hostiaci_tim?.nazov ||
                  zapas.hostiaci_tim?.skratka ||
                  "TBD"}
              </p>
            </div>
          </div>
        </div>

        {/* Čas zápasu */}
        {zapas.stav_typ === "STATUS_SCHEDULED" && (
          <p className="text-xs text-muted-foreground mt-4 text-center">
            {new Date(zapas.datum_zaciatku).toLocaleTimeString("sk-SK", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function NhlLivePage() {
  const router = useRouter();
  const [zapasy, setZapasy] = useState<NhlZapasData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [, setKalendar] = useState<string[]>([]);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [source, setSource] = useState<string>("");

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(
        `/api/nhl/nhl-live?date=${selectedDate}&_t=${Date.now()}`,
        {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: ApiResponse = await response.json();

      setZapasy(data.zapasy || []);
      setSource(data.source);
      setLastUpdate(new Date());

      if (data.kalendar && data.kalendar.length > 0) {
        setKalendar(data.kalendar);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Neznáma chyba";
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate]);

  // Načítanie pri štarte a zmene dátumu
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh každých 30s
  useEffect(() => {
    const interval = setInterval(fetchData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Navigácia dátumom
  const navigateDate = (direction: "prev" | "next") => {
    const currentDate = new Date(selectedDate);
    if (direction === "prev") {
      currentDate.setDate(currentDate.getDate() - 1);
    } else {
      currentDate.setDate(currentDate.getDate() + 1);
    }
    setSelectedDate(currentDate.toISOString().split("T")[0]);
  };

  const jeAktualnyDen = selectedDate === new Date().toISOString().split("T")[0];

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-4 px-3 sm:px-4 max-w-4xl">
        {/* Hlavička */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl sm:text-2xl font-bold">NHL Live</h1>
          <div className="flex items-center gap-2">
            {lastUpdate && (
              <span className="text-xs text-muted-foreground hidden sm:inline">
                {lastUpdate.toLocaleTimeString("sk-SK")}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              disabled={isLoading}
              className="border-border bg-transparent"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Navigácia dátumom */}
        <div className="flex items-center justify-between bg-card/50 rounded-lg border border-border p-3 mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateDate("prev")}
            className="hover:bg-muted/10"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-sm sm:text-base">
              {new Date(selectedDate + "T12:00:00").toLocaleDateString(
                "sk-SK",
                {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                },
              )}
            </span>
            {jeAktualnyDen && (
              <Badge variant="secondary" className="text-xs">
                Dnes
              </Badge>
            )}
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateDate("next")}
            className="hover:bg-muted/10"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Chybový stav */}
        {error && (
          <Card className="border-destructive/50 mb-4">
            <CardContent className="py-4 text-center">
              <p className="text-destructive text-sm mb-2">{error}</p>
              <Button variant="outline" size="sm" onClick={fetchData}>
                Skúsiť znovu
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Loading */}
        {isLoading && zapasy.length === 0 && (
          <Card className="border-border">
            <CardContent className="py-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary" />
              <p className="text-muted-foreground text-sm">Načítavam...</p>
            </CardContent>
          </Card>
        )}

        {/* Zápasy */}
        {!isLoading && zapasy.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-2">
              <Badge variant="outline" className="text-xs">
                {zapasy.length} zápasov
              </Badge>
              {source && (
                <span className="text-xs text-muted-foreground">
                  {source.includes("database") ? "DB" : "Live"}
                </span>
              )}
            </div>

            <div className="grid gap-3">
              {zapasy.map((zapas) => (
                <ZapasKarta
                  key={zapas.espn_game_id}
                  zapas={zapas}
                  onClick={() => router.push(`/live-nhl/${zapas.espn_game_id}`)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Prázdny stav */}
        {!isLoading && zapasy.length === 0 && !error && (
          <Card className="border-border">
            <CardContent className="py-12 text-center">
              <Calendar className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground font-medium">
                Žiadne zápasy pre {selectedDate}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Skúste iný dátum
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
